import currencyLib from "currency.js";
import { LoyaltyItemOffer } from "../LoyaltyOfferClass/LoyaltyItemOffer";
import { LoyaltyRewardDistribution, LoyaltyValueType } from "../loyaltyTypes";
import { OfferSelectionGroupKind, SelectedEntry } from "./offerSelection";

/**
 * Display pricing for the offer page. Presentation only —
 * `POST /cart/calculate` stays authoritative for what the guest is charged.
 */

export type OfferItemPrice = {
  price: number;
  /** Full price, set only when a discount applies and should be struck through. */
  priceBeforeDiscount?: number;
  /** Given away outright: render the offer's label chip instead of a price. */
  isFree: boolean;
};

/**
 * The reward is priced as a set, so no single item carries the discount.
 *
 * Two shapes do this. A NEW_VALUE offer restates the whole reward as one
 * figure. An AGGREGATE one takes its value off the set once — "EGP 30 off the
 * pair", not 30 off each — which the service applies as a proportional split
 * across the matched lines. Either way there is no per-item discount to draw,
 * so the items show their own price and the saving belongs to the total.
 *
 * The service only allows `rewardDistribution` on FIXED and NEW_VALUE, so
 * PERCENTAGE never reaches here — and per-item is equivalent for it anyway.
 */
function isSetPricedOffer(offer: LoyaltyItemOffer): boolean {
  return (
    offer.valueType === LoyaltyValueType.NEW_VALUE || isAggregateReward(offer)
  );
}

/** The value comes off the reward set once, not off each item in it. */
function isAggregateReward(offer: LoyaltyItemOffer): boolean {
  return (
    offer.getRewardTargetConfig()?.rewardDistribution ===
    LoyaltyRewardDistribution.AGGREGATE
  );
}

function discountedUnitPrice(
  offer: LoyaltyItemOffer,
  fullPrice: number
): number {
  switch (offer.valueType) {
    case LoyaltyValueType.PERCENTAGE:
      return currencyLib(fullPrice).multiply(
        currencyLib(100).subtract(offer.value).divide(100).value
      ).value;

    case LoyaltyValueType.FIXED:
      return Math.max(currencyLib(fullPrice).subtract(offer.value).value, 0);

    case LoyaltyValueType.NEW_VALUE:
    default:
      return fullPrice;
  }
}

/** Buy-side items are never discounted — paying for them earns the reward. */
export function offerItemPrice(
  offer: LoyaltyItemOffer,
  entry: Pick<SelectedEntry, "group"> & { fullPrice: number }
): OfferItemPrice {
  const { fullPrice } = entry;

  if (entry.group === OfferSelectionGroupKind.Buy) {
    return { price: fullPrice, isFree: false };
  }

  if (offer.isFreeReward()) {
    return { price: 0, isFree: true };
  }

  if (isSetPricedOffer(offer)) {
    return { price: fullPrice, isFree: false };
  }

  const price = discountedUnitPrice(offer, fullPrice);

  return {
    price,
    ...(price < fullPrice && { priceBeforeDiscount: fullPrice }),
    isFree: false,
  };
}

/**
 * What a fixed bundle costs once its offer applies, given what its items cost
 * on their own.
 *
 * A bundle is always AGGREGATE, so its value lands on the set once: NEW_VALUE
 * restates the price outright, FIXED comes off the subtotal, and PERCENTAGE
 * scales it. `maxRewardAmount` caps the saving the same way it does on the
 * offer page, so a card never promises more than the cart will give.
 */
export function fixedBundleTotal(
  offer: LoyaltyItemOffer,
  subtotal: number
): number {
  const total =
    offer.valueType === LoyaltyValueType.NEW_VALUE
      ? currencyLib(offer.value)
      : offer.valueType === LoyaltyValueType.PERCENTAGE
      ? currencyLib(subtotal).multiply(
          currencyLib(100).subtract(offer.value).divide(100).value
        )
      : currencyLib(
          Math.max(currencyLib(subtotal).subtract(offer.value).value, 0)
        );

  const discount = currencyLib(subtotal).subtract(total.value).value;
  if (offer.maxRewardAmount && discount > offer.maxRewardAmount) {
    return currencyLib(subtotal).subtract(offer.maxRewardAmount).value;
  }

  return Math.max(total.value, 0);
}

type OfferSelectionTotals = {
  totalBeforeOffer: number;
  total: number;
  discount: number;
};

/** Addon spend on the chosen items, split by which side of the offer they sit on. */
type OfferAddonTotals = {
  buy: number;
  reward: number;
};

/**
 * CTA total for the current selection: buy side at full price, reward side
 * discounted and capped by `maxRewardAmount`.
 *
 * `applicationScope.includeAddons` extends a percentage reward to the addons
 * on it. A fixed amount or a replacement price already restates the whole
 * reward line, so addons stay at full price in those cases.
 */
export function offerSelectionTotals(
  offer: LoyaltyItemOffer,
  entries: SelectedEntry[],
  getFullPrice: (entry: SelectedEntry) => number,
  addons: OfferAddonTotals = { buy: 0, reward: 0 }
): OfferSelectionTotals {
  let buySubtotal = currencyLib(0);
  let rewardSubtotal = currencyLib(0);

  for (const entry of entries) {
    const lineTotal = currencyLib(getFullPrice(entry)).multiply(entry.quantity);

    if (entry.group === OfferSelectionGroupKind.Buy) {
      buySubtotal = buySubtotal.add(lineTotal);
    } else {
      rewardSubtotal = rewardSubtotal.add(lineTotal);
    }
  }

  const totalBeforeOffer = buySubtotal
    .add(rewardSubtotal)
    .add(addons.buy)
    .add(addons.reward);

  const rewardAfterDiscount =
    // NEW_VALUE restates the set's price, so the value *is* the total; an
    // AGGREGATE amount comes off it once. Order matters — a bundle can be
    // both, and "2 for 150" means 150, not 150 off.
    offer.valueType === LoyaltyValueType.NEW_VALUE
      ? currencyLib(offer.value)
      : isAggregateReward(offer)
      ? // Never below zero: a value larger than the reward is a
        // misconfiguration, not a credit.
        currencyLib(Math.max(rewardSubtotal.subtract(offer.value).value, 0))
      : entries.reduce((sum, entry) => {
          if (entry.group === OfferSelectionGroupKind.Buy) {
            return sum;
          }

          const unit = offer.isFreeReward()
            ? 0
            : discountedUnitPrice(offer, getFullPrice(entry));

          return sum.add(currencyLib(unit).multiply(entry.quantity));
        }, currencyLib(0));

  const discountsAddons =
    Boolean(offer.applicationScope?.includeAddons) &&
    (offer.isFreeReward() || offer.valueType === LoyaltyValueType.PERCENTAGE);

  const rewardAddonsAfterDiscount = !discountsAddons
    ? currencyLib(addons.reward)
    : offer.isFreeReward()
    ? currencyLib(0)
    : currencyLib(discountedUnitPrice(offer, addons.reward));

  let discount = rewardSubtotal
    .add(addons.reward)
    .subtract(rewardAfterDiscount)
    .subtract(rewardAddonsAfterDiscount);

  // A bundle priced above the sum of its parts is a misconfiguration; never
  // show a negative saving.
  if (discount.value < 0) {
    discount = currencyLib(0);
  }

  if (offer.maxRewardAmount && discount.value > offer.maxRewardAmount) {
    discount = currencyLib(offer.maxRewardAmount);
  }

  return {
    totalBeforeOffer: totalBeforeOffer.value,
    total: totalBeforeOffer.subtract(discount).value,
    discount: discount.value,
  };
}
